/**
 * auth-helpers.ts — Kiểm tra form đăng ký / đặt lại mật khẩu, độ mạnh mật khẩu, điền sẵn mã giới thiệu.
 * Server kiểm lại tất cả (ecdp_backend_source/src/modules/auth/signup/signup-validate.ts là nguồn chuẩn).
 */
import { normalizeCode, parseStoredRef, REF_STORAGE_KEY } from '@/lib/affiliate'
import { i18n } from '@/lib/i18n'

export const PASSWORD_MIN = 8

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase()
}

export function isValidEmail(raw: string): boolean {
  const e = normalizeEmail(raw)
  return e.length <= 160 && EMAIL_RE.test(e)
}

/** SĐT Việt Nam hợp lệ (0xxxxxxxxx / +84 / 84; di động 03/05/07/08/09, cố định 02). */
export function isValidVnPhone(raw: string): boolean {
  let d = raw.replace(/[\s.\-()]/g, '')
  if (d.startsWith('+')) d = d.slice(1)
  if (!/^\d+$/.test(d)) return false
  if (d.startsWith('84')) d = `0${d.slice(2)}`
  return /^0[235789]\d{8}$/.test(d)
}

export type StrengthLevel = 0 | 1 | 2 | 3 | 4

export interface PasswordStrength {
  level: StrengthLevel
  label: string
  hint: string | null
}

/** Độ mạnh mật khẩu (gợi ý, không chặn trừ khi < 8 ký tự). */
export function passwordStrength(pw: string): PasswordStrength {
  const t = (k: string, o?: { count: number }) => i18n.t(`authPages:passwordStrength.${k}`, o)
  if (!pw) return { level: 0, label: '', hint: t('minHint', { count: PASSWORD_MIN }) }
  if (pw.length < PASSWORD_MIN) return { level: 1, label: t('tooShort'), hint: t('minHint', { count: PASSWORD_MIN }) }
  let kinds = 0
  if (/[a-z]/.test(pw)) kinds++
  if (/[A-Z]/.test(pw)) kinds++
  if (/\d/.test(pw)) kinds++
  if (/[^A-Za-z0-9]/.test(pw)) kinds++
  const common = /^(12345678|123456789|password|matkhau|qwerty|abc12345|11111111)/i.test(pw)
  let score = kinds + (pw.length >= 12 ? 1 : 0)
  if (common) score = 1
  if (score <= 1) return { level: 1, label: t('weak'), hint: t('weakHint') }
  if (score === 2) return { level: 2, label: t('medium'), hint: t('mediumHint') }
  if (score === 3) return { level: 3, label: t('good'), hint: null }
  return { level: 4, label: t('strong'), hint: null }
}

export function passwordError(pw: string, email?: string): string | null {
  const t = (k: string, o?: { count: number }) => i18n.t(`authPages:validate.${k}`, o)
  if (pw.length < PASSWORD_MIN) return t('passwordMinLength', { count: PASSWORD_MIN })
  if (new TextEncoder().encode(pw).length > 72) return t('passwordMaxLength')
  if (!pw.trim()) return t('passwordNoSpaceOnly')
  if (email && pw.trim().toLowerCase() === normalizeEmail(email)) return t('passwordSameAsEmail')
  return null
}

export interface SignupForm {
  fullName: string
  email: string
  phone: string
  orgName: string
  password: string
  confirmPassword: string
  referralCode: string
  acceptTerms: boolean
  /** honeypot — người thật để trống */
  website: string
}

export const EMPTY_SIGNUP_FORM: SignupForm = {
  fullName: '', email: '', phone: '', orgName: '', password: '', confirmPassword: '', referralCode: '', acceptTerms: false, website: '',
}

export type SignupErrors = Partial<Record<keyof SignupForm, string>>

/** Kiểm form đăng ký. `google` = bước "Hoàn tất đăng ký" (không có email / mật khẩu). */
export function validateSignupForm(f: SignupForm, opts: { google?: boolean } = {}): SignupErrors {
  const t = (k: string) => i18n.t(`authPages:validate.${k}`)
  const e: SignupErrors = {}
  if (f.fullName.trim().length < 2) e.fullName = t('fullNameRequired')
  if (!opts.google && !isValidEmail(f.email)) e.email = t('emailInvalid')
  if (!isValidVnPhone(f.phone)) e.phone = t('phoneInvalid')
  if (f.orgName.trim().length < 2) e.orgName = t('orgNameRequired')
  if (!opts.google) {
    const pe = passwordError(f.password, f.email)
    if (pe) e.password = pe
    else if (f.confirmPassword !== f.password) e.confirmPassword = t('confirmPasswordMismatch')
  }
  if (f.referralCode.trim() && !/^[A-Z0-9][A-Z0-9-]{3,19}$/.test(normalizeCode(f.referralCode))) {
    e.referralCode = t('referralCodeInvalid')
  }
  if (!f.acceptTerms) e.acceptTerms = t('acceptTermsRequired')
  return e
}

/** Mã OTP: giữ lại chữ số, tối đa 6. */
export function sanitizeOtp(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 6)
}

export interface RefPrefill {
  code: string
  /** vé ký bởi server (chỉ khi vé trình duyệt cùng mã) */
  ticket: string | null
}

/**
 * Điền sẵn "Mã giới thiệu": `?ref=` trên URL ưu tiên (khách vừa bấm link có mã), sau đó vé đã lưu `ecdp_ref`
 * (trang /r/:mã — first/last-touch đã áp khi lưu). Vé chỉ gửi kèm khi cùng mã.
 */
export function refPrefill(search: string, storedRaw: string | null, now = Date.now()): RefPrefill | null {
  const stored = parseStoredRef(storedRaw, now)
  const q = new URLSearchParams(search).get('ref')
  const fromQuery = q ? normalizeCode(q).slice(0, 20) : ''
  if (fromQuery) return { code: fromQuery, ticket: stored && stored.code === fromQuery ? stored.ticket : null }
  if (stored) return { code: stored.code, ticket: stored.ticket }
  return null
}

export function readRefPrefill(search: string): RefPrefill | null {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(REF_STORAGE_KEY)
  } catch {
    raw = null
  }
  return refPrefill(search, raw)
}

/**
 * Email điền sẵn ở form đăng ký từ trang giới thiệu (ô "Bắt đầu miễn phí"): `/register#email=ban@congty.vn`.
 * Đọc từ phần `#` (không gửi lên máy chủ, không vào log); chỉ nhận chuỗi giống email, tối đa 160 ký tự.
 */
export function emailFromHash(hash: string): string {
  let v: string | null = null
  try { v = new URLSearchParams(hash.replace(/^#/, '')).get('email') } catch { return '' }
  v = (v ?? '').trim()
  return v.length <= 160 && isValidEmail(v) ? v : ''
}

/** Vé gửi lên server: chỉ khi mã trên form vẫn là mã của vé. */
export function ticketFor(code: string, prefill: RefPrefill | null): string | null {
  if (!prefill?.ticket) return null
  return normalizeCode(code) === prefill.code || !code.trim() ? prefill.ticket : null
}

/** Đếm ngược "Gửi lại mã sau Ns". */
export function formatCountdown(sec: number): string {
  const s = Math.max(0, Math.ceil(sec))
  const m = Math.floor(s / 60)
  return m > 0 ? `${m}:${String(s % 60).padStart(2, '0')}` : `${s}s`
}

/** Che email hiển thị ở bước nhập mã: ng***@gmail.com */
export function maskEmail(email: string): string {
  const [local, domain] = email.split('@')
  if (!domain) return email
  return `${local.slice(0, 2)}***@${domain}`
}
